"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getContentAsString = exports.getContentAsBuffer = exports.isWhitedOutFile = exports.getUserInstructionLayersFromConfig = exports.getDetectedLayersInfoFromConfig = exports.getPlatformFromConfig = exports.getRootFsLayersFromConfig = exports.extractImageContent = exports.InvalidArchiveError = void 0;
const Debug = require("debug");
const path = require("path");
const instruction_parser_1 = require("../dockerfile/instruction-parser");
const error_utils_1 = require("../error-utils");
const option_utils_1 = require("../option-utils");
const types_1 = require("../types");
const dockerExtractor = require("./docker-archive");
const generic_archive_extractor_1 = require("./generic-archive-extractor");
const kanikoExtractor = require("./kaniko-archive");
const layer_1 = require("./layer");
const ociExtractor = require("./oci-archive");
const debug = Debug("snyk");
var generic_archive_extractor_2 = require("./generic-archive-extractor");
Object.defineProperty(exports, "InvalidArchiveError", { enumerable: true, get: function () { return generic_archive_extractor_2.InvalidArchiveError; } });
class ArchiveExtractor {
    constructor(extractor, path, actions, options) {
        this.fileSystemPath = path;
        this.extractActions = actions;
        this.extractor = extractor;
        this.options = options;
    }
    getExtractor() {
        return this.extractor;
    }
    async getLayersAndManifest() {
        return await this.extractor.extractArchive(this.fileSystemPath, this.extractActions, this.options);
    }
    getImageIdFromManifest(manifest) {
        return this.extractor.getImageIdFromManifest(manifest);
    }
    getManifestLayers(manifest) {
        return this.extractor.getManifestLayers(manifest);
    }
}
/**
 * Given a path on the file system to a image archive, open it up to inspect the layers
 * and look for specific files. File content can be transformed with a custom callback function if needed.
 * @param fileSystemPath Path to an existing archive.
 * @param extractActions This denotes a file pattern to look for and how to transform the file if it is found.
 * By default the file is returned raw if no processing is desired.
 */
async function extractImageContent(imageType, fileSystemPath, extractActions, options) {
    var _a;
    const extractors = new Map([
        [
            types_1.ImageType.DockerArchive,
            new ArchiveExtractor(dockerExtractor, fileSystemPath, extractActions, options),
        ],
        [
            types_1.ImageType.OciArchive,
            new ArchiveExtractor(ociExtractor, fileSystemPath, extractActions, options),
        ],
        [
            types_1.ImageType.KanikoArchive,
            new ArchiveExtractor(kanikoExtractor, fileSystemPath, extractActions, options),
        ],
    ]);
    let extractor;
    let archiveContent;
    if (!extractors.has(imageType)) {
        // Unknown image type - try all extractors to auto-detect format
        [archiveContent, extractor] = await extractArchiveContentFallback(extractors);
    }
    else {
        extractor = extractors.get(imageType);
        try {
            archiveContent = await extractor.getLayersAndManifest();
        }
        catch (err) {
            if (err instanceof generic_archive_extractor_1.InvalidArchiveError) {
                // fallback to the other extractor if layer extraction failed
                [archiveContent, extractor] = await extractArchiveContentFallback(extractors);
            }
            else {
                throw err;
            }
        }
    }
    return {
        imageId: extractor.getImageIdFromManifest(archiveContent.manifest),
        manifestLayers: extractor.getManifestLayers(archiveContent.manifest),
        imageCreationTime: archiveContent.imageConfig.created,
        extractedLayers: layersWithLatestFileModifications(archiveContent.layers),
        // `archiveContent.layers` is top→bottom here; `orderedLayers` is FROM→top
        // (see its doc on `ExtractionResult`). Copy before reversing so we don't
        // mutate the array `layersWithLatestFileModifications` consumed above.
        orderedLayers: (0, option_utils_1.isTrue)(options === null || options === void 0 ? void 0 : options["layer-attribution"])
            ? [...archiveContent.layers].reverse()
            : undefined,
        rootFsLayers: getRootFsLayersFromConfig(archiveContent.imageConfig),
        autoDetectedUserInstructions: getDetectedLayersInfoFromConfig(archiveContent.imageConfig),
        platform: getPlatformFromConfig(archiveContent.imageConfig),
        imageLabels: (_a = archiveContent.imageConfig.config) === null || _a === void 0 ? void 0 : _a.Labels,
        containerConfig: archiveContent.imageConfig.config,
        history: archiveContent.imageConfig.history,
    };
}
exports.extractImageContent = extractImageContent;
async function extractArchiveContentFallback(extractors) {
    for (const [imageType, extractor] of extractors.entries()) {
        try {
            return [await extractor.getLayersAndManifest(), extractor];
        }
        catch (error) {
            // imageType is a string enum value like "docker-archive", "oci-archive"
            debug(`Error getting layers and manifest content from ${imageType} archive: ${(0, error_utils_1.getErrorMessage)(error)}`);
            continue;
        }
    }
    throw new generic_archive_extractor_1.InvalidArchiveError(`Unsupported archive type. Please use a Docker archive, OCI image layout, or Kaniko-compatible tarball.`);
}
function getRootFsLayersFromConfig(imageConfig) {
    try {
        return imageConfig.rootfs.diff_ids;
    }
    catch (err) {
        throw new Error("Failed to extract rootfs array from image config");
    }
}
exports.getRootFsLayersFromConfig = getRootFsLayersFromConfig;
function getPlatformFromConfig(imageConfig) {
    return (imageConfig === null || imageConfig === void 0 ? void 0 : imageConfig.os) && (imageConfig === null || imageConfig === void 0 ? void 0 : imageConfig.architecture)
        ? `${imageConfig.os}/${imageConfig.architecture}`
        : undefined;
}
exports.getPlatformFromConfig = getPlatformFromConfig;
function getDetectedLayersInfoFromConfig(imageConfig) {
    const runInstructions = getUserInstructionLayersFromConfig(imageConfig)
        .filter((instruction) => !instruction.empty_layer && instruction.created_by)
        .map((instruction) => instruction.created_by.replace("# buildkit", ""));
    const dockerfilePackages = (0, instruction_parser_1.getPackagesFromRunInstructions)(runInstructions);
    const dockerfileLayers = (0, instruction_parser_1.getLayersFromPackages)(dockerfilePackages);
    return { dockerfilePackages, dockerfileLayers };
}
exports.getDetectedLayersInfoFromConfig = getDetectedLayersInfoFromConfig;
function getUserInstructionLayersFromConfig(imageConfig) {
    const diffInHours = (d1, d2) => Math.abs(d1 - d2) / 1000 / (60 * 60);
    const maxDiffInHours = 5;
    const history = imageConfig.history;
    if (!history || history.length === 0) {
        return [];
    }
    const lastInstructionTime = new Date(history.slice(-1)[0].created);
    const userInstructionLayers = history.filter((layer) => {
        return (diffInHours(new Date(layer.created), lastInstructionTime) <=
            maxDiffInHours);
    });
    // should only happen if there are no layers created by user instructions
    if (userInstructionLayers.length === history.length) {
        return [];
    }
    return userInstructionLayers;
}
exports.getUserInstructionLayersFromConfig = getUserInstructionLayersFromConfig;
function layersWithLatestFileModifications(layers) {
    const extractedLayers = {};
    const removedFilesToIgnore = new Set();
    // TODO: This removes the information about the layer name, maybe we would need it in the future?
    for (const layer of layers) {
        // go over extracted files products found in this layer
        for (const filename of Object.keys(layer)) {
            // if finding a deleted file - trimming to its original file name for excluding it from extractedLayers
            // + not adding this file
            if ((0, layer_1.isWhitedOutFile)(filename)) {
                removedFilesToIgnore.add(filename.replace(/.wh./, ""));
                continue;
            }
            // not adding previously found to be whited out files to extractedLayers
            if (removedFilesToIgnore.has(filename)) {
                continue;
            }
            // not adding path that has removed path as parent
            if (isFileInARemovedFolder(filename, removedFilesToIgnore)) {
                continue;
            }
            // file not already in extractedLayers
            if (!Reflect.has(extractedLayers, filename)) {
                extractedLayers[filename] = layer[filename];
            }
        }
    }
    return extractedLayers;
}
var layer_2 = require("./layer");
Object.defineProperty(exports, "isWhitedOutFile", { enumerable: true, get: function () { return layer_2.isWhitedOutFile; } });
function isBufferType(type) {
    return type.buffer !== undefined;
}
function isStringType(type) {
    return type.substring !== undefined;
}
function getContentAsBuffer(extractedLayers, extractAction) {
    const content = getContent(extractedLayers, extractAction);
    return content !== undefined && isBufferType(content) ? content : undefined;
}
exports.getContentAsBuffer = getContentAsBuffer;
function getContentAsString(extractedLayers, extractAction) {
    const content = getContent(extractedLayers, extractAction);
    return content !== undefined && isStringType(content) ? content : undefined;
}
exports.getContentAsString = getContentAsString;
function getContent(extractedLayers, extractAction) {
    const fileNames = Object.keys(extractedLayers);
    const fileNamesProducedByTheExtractAction = fileNames.filter((name) => extractAction.actionName in extractedLayers[name]);
    const firstFileNameMatch = fileNamesProducedByTheExtractAction.find((match) => extractAction.filePathMatches(match));
    return firstFileNameMatch !== undefined
        ? extractedLayers[firstFileNameMatch][extractAction.actionName]
        : undefined;
}
function isFileInFolder(file, folder) {
    const folderPath = path.normalize(folder);
    const filePath = path.normalize(file);
    return filePath.startsWith(path.join(folderPath, path.sep));
}
function isFileInARemovedFolder(filename, removedFilesToIgnore) {
    return Array.from(removedFilesToIgnore).some((removedFile) => isFileInFolder(filename, removedFile));
}
//# sourceMappingURL=index.js.map